import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Upload, Loader2, Save, Store } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [logoFileId, setLogoFileId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user?.workspace) {
      setName(user.workspace.name || "");
      setLogoFileId(user.workspace.logo_file_id || null);
    }
  }, [user, navigate]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Maks 15MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoFileId(data.file_id);
      toast.success("Logo terupload. Klik Simpan untuk terapkan.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload gagal");
    } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/workspaces/current", { name: name.trim(), logo_file_id: logoFileId });
      toast.success("Pengaturan tersimpan");
      await checkAuth();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };

  const token = typeof window !== "undefined" ? localStorage.getItem("session_token") : null;
  const logoUrl = logoFileId ? `${API}/files/${logoFileId}${token ? `?token=${encodeURIComponent(token)}` : ""}` : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pengaturan Usaha"
        subtitle="Ganti nama usaha dan logo. Perubahan berlaku untuk semua pengguna di usaha ini."
      />
      <Card className="p-6 border-slate-200 space-y-6 max-w-2xl">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-xl bg-emerald-500 flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-16 w-16 object-cover" data-testid="settings-logo-preview" />
            ) : (
              <Store className="h-8 w-8 text-white" />
            )}
          </div>
          <div>
            <div className="font-heading text-base font-bold text-slate-900">Logo Usaha</div>
            <div className="text-xs text-slate-500 mt-0.5">PNG/JPG maks 15MB. Rasio 1:1 direkomendasikan.</div>
            <label className="mt-2 inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Mengunggah..." : "Unggah Logo"}
              <input type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="settings-logo-input" />
            </label>
            {logoFileId && (
              <button onClick={() => setLogoFileId(null)} className="ml-2 text-xs text-rose-600 hover:underline" data-testid="settings-logo-clear">
                Hapus logo
              </button>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ws-name" className="text-sm text-slate-700">Nama Usaha</Label>
          <Input id="ws-name" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Contoh: Toko Kelontong Sejahtera" data-testid="settings-name-input" />
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button onClick={save} disabled={saving || !name.trim() || user?.role === "cabang"}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="settings-save-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Menyimpan..." : user?.role === "cabang" ? "Hanya admin" : "Simpan Perubahan"}
          </Button>
        </div>
      </Card>

      {user?.role === "super_admin" && <WorkspacesPanel />}
    </div>
  );
}

function WorkspacesPanel() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/workspaces"); setRows(data); } catch { /* ignore */ } };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workspaces", { name: name.trim() });
      toast.success("Usaha baru dibuat");
      setName(""); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const switchTo = async (id) => {
    try {
      await api.post("/workspaces/switch", { workspace_id: id });
      toast.success("Berpindah usaha");
      window.location.reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id, nm) => {
    if (!window.confirm(`Hapus usaha "${nm}"?`)) return;
    try { await api.delete(`/workspaces/${id}`); toast.success("Usaha dihapus"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
  };

  return (
    <Card className="p-6 border-slate-200 max-w-2xl">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="h-4 w-4 text-emerald-600" />
        <div className="font-heading text-base font-bold text-slate-900">Daftar Usaha (Multi Usaha)</div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Super admin dapat membuat beberapa usaha terpisah dan berpindah antar usaha. Data & cabang setiap usaha terpisah.</p>
      <form onSubmit={add} className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama usaha baru" required data-testid="workspace-new-name" />
        <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="workspace-add-btn">
          Buat
        </Button>
      </form>
      <ul className="space-y-2" data-testid="workspace-list">
        {rows.map((w) => (
          <li key={w.id} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2">
            <div>
              <div className="text-sm font-semibold">{w.name}</div>
              <div className="text-[11px] text-slate-400">Dibuat oleh {w.created_by}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => switchTo(w.id)} data-testid={`workspace-switch-${w.id}`}>Pilih</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(w.id, w.name)} className="text-rose-600" data-testid={`workspace-delete-${w.id}`}>Hapus</Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
